import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface RaporItemDef {
    id: string; title: string; desc: string; category: string; isNew?: boolean;
    newBadgeStyle?: React.CSSProperties; iconBg: string; iconBorder: string;
    icon: React.ReactNode; accentColor: string; accentShadow: string; onClick: () => void;
}

const RaporKartItem: React.FC<{
    r: RaporItemDef;
    isFavori: boolean;
    onToggleFavori: (e: React.MouseEvent, id: string) => void;
    cardStyle: React.CSSProperties;
}> = ({ r, isFavori, onToggleFavori, cardStyle }) => {
    const [hovered, setHovered] = React.useState(false);
    return (
        <div
            className="rapor-hub-card"
            onClick={r.onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                ...cardStyle,
                boxShadow: hovered ? `0 4px 16px ${r.accentShadow}` : '0 1px 3px rgba(0,0,0,0.04)',
                borderColor: hovered ? r.accentColor : '#e2e8f0',
                transform: hovered ? 'translateY(-2px)' : 'none',
            }}
        >
            <div style={{ width: 48, height: 48, borderRadius: 12, background: r.iconBg, border: `1px solid ${r.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {r.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {r.title}
                    {r.isNew && (
                        <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 10, fontWeight: 600, ...(r.newBadgeStyle || { background: '#f3e8ff', color: '#7c3aed', border: '1px solid #e9d5ff' }) }}>Yeni</span>
                    )}
                </h4>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.82rem' }}>{r.desc}</p>
            </div>
            <button
                className={`rapor-star-btn${isFavori ? ' active' : ''}`}
                onClick={(e) => onToggleFavori(e, r.id)}
                title={isFavori ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            >
                <svg width="17" height="17" viewBox="0 0 24 24" fill={isFavori ? '#f59e0b' : 'none'} stroke={isFavori ? '#f59e0b' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </button>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke={hovered ? r.accentColor : '#94a3b8'} width={20} height={20} style={{ flexShrink: 0, transition: 'stroke 0.2s ease' }}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
        </div>
    );
};

const ProjeRaporlari: React.FC = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState<'Tümü' | 'Stok' | 'Fiyat' | 'Performans'>('Tümü');
    const [searchQuery, setSearchQuery] = useState('');
    const [favoriRaporlar, setFavoriRaporlar] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem('projeRaporlariFavori') || '[]'); } catch { return []; }
    });

    const toggleFavori = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setFavoriRaporlar(prev => {
            const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
            localStorage.setItem('projeRaporlariFavori', JSON.stringify(next));
            return next;
        });
    };

    const cardStyle: React.CSSProperties = {
        background: '#fff', borderRadius: '16px', padding: '24px',
        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: 20, position: 'relative'
    };

    const raporListesi: RaporItemDef[] = [
        {
            id: 'stok-erime-hizi',
            title: 'Stok Erime Hızı Raporu',
            desc: 'Bağımsız bölümlerin satış hızını ve envanter회전 performansını analiz edin',
            category: 'Stok',
            isNew: true,
            newBadgeStyle: { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' },
            iconBg: '#f0f9ff', iconBorder: '#bae6fd', accentColor: '#0284c7', accentShadow: 'rgba(2,132,199,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
            onClick: () => {
                navigate('/stok-erime-hizi');
            },
        },
        {
            id: 'stokta-bekleme',
            title: 'Stokta Bekleme Raporu',
            desc: 'Satılmayan dairelerin bekleme süresini ve verimsiz stokları görüntüleyin',
            category: 'Stok',
            isNew: true,
            newBadgeStyle: { background: '#fef9c3', color: '#a16207', border: '1px solid #fde047' },
            iconBg: '#fffbeb', iconBorder: '#fde68a', accentColor: '#d97706', accentShadow: 'rgba(217,119,6,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
            onClick: () => {
                navigate('/stok-bekleme');
            },
        },
        {
            id: 'fiyat-degisim-iskonto',
            title: 'Fiyat Değişim ve İskonto Performans Raporu',
            desc: 'Fiyat değişikliklerinin ve iskonto stratejilerinin satış başarısına etkisini ölçün',
            category: 'Fiyat',
            isNew: true,
            newBadgeStyle: { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' },
            iconBg: '#f0fdf4', iconBorder: '#bbf7d0', accentColor: '#16a34a', accentShadow: 'rgba(22,163,74,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            onClick: () => {
                // TODO: Fiyat değişim ve iskonto performans raporu sayfası
                console.log('Fiyat Değişim ve İskonto Performans Raporu açılacak');
            },
        },
    ];

    const filteredRaporlar = raporListesi.filter(r => {
        const categoryMatch = activeCategory === 'Tümü' || r.category === activeCategory;
        const searchMatch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.desc.toLowerCase().includes(searchQuery.toLowerCase());
        return categoryMatch && searchMatch;
    });

    const favoriRaporlarItems = raporListesi.filter(r => favoriRaporlar.includes(r.id));

    return (
        <div style={{ padding: '24px', fontFamily: "'Inter', 'Roboto', sans-serif", color: '#1f2937' }}>
            <style>{`
                .rapor-kartlar-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
                @media (max-width: 600px) {
                    .rapor-kartlar-grid { grid-template-columns: 1fr; gap: 12px; }
                }
                .hub-category-pill { cursor: pointer; padding: 6px 18px; border-radius: 999px; font-size: 0.82rem; font-weight: 600; border: 1.5px solid transparent; transition: all 0.15s; }
                .hub-category-pill:hover { opacity: 0.85; }
                .rapor-hub-card { display: flex; align-items: center; gap: 20px; cursor: pointer; transition: all 0.2s ease; position: relative; }
                .rapor-hub-card:hover { transform: translateY(-2px); }
                .rapor-star-btn { background: none; border: none; cursor: pointer; padding: 4px; border-radius: 6px; transition: color 0.15s, background 0.15s; color: #cbd5e1; flex-shrink: 0; }
                .rapor-star-btn:hover { color: #f59e0b; background: #fef9c3; }
                .rapor-star-btn.active { color: #f59e0b; }
            `}</style>

            {/* === BAŞLIK === */}
            <div style={{ marginBottom: '28px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: '0 0 4px 0', letterSpacing: '-0.025em' }}>
                    Raporlar
                </h1>
                <p style={{ fontSize: '13.5px', color: '#6b7280', margin: 0 }}>
                    Proje performans, stok ve fiyatlandırma analizlerinizi tek merkezden yönetin.
                </p>
            </div>

            {/* === FİLTRE BARI === */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                {/* Kategori Pilleri */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(['Tümü', 'Stok', 'Fiyat', 'Performans'] as const).map(cat => (
                        <button
                            key={cat}
                            className="hub-category-pill"
                            onClick={() => setActiveCategory(cat)}
                            style={{
                                background: activeCategory === cat ? '#1e293b' : '#f8fafc',
                                color: activeCategory === cat ? '#fff' : '#64748b',
                                borderColor: activeCategory === cat ? '#1e293b' : '#e2e8f0',
                            }}
                        >
                            {cat === 'Tümü' ? 'Tümü' : cat === 'Stok' ? 'Stok Raporları' : cat === 'Fiyat' ? 'Fiyat Analizleri' : 'Performans Raporları'}
                        </button>
                    ))}
                </div>
                {/* Arama */}
                <div style={{ position: 'relative' }}>
                    <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        placeholder="Rapor ara…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: '32px', paddingRight: '12px', paddingTop: '7px', paddingBottom: '7px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.83rem', color: '#374151', outline: 'none', background: '#fff', width: '200px' }}
                    />
                </div>
            </div>

            {/* === FAVORI RAPORLAR === */}
            {favoriRaporlarItems.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sık Kullanılan Raporlar</span>
                    </div>
                    <div className="rapor-kartlar-grid">
                        {favoriRaporlarItems.map(r => (
                            <RaporKartItem key={'fav-' + r.id} r={r} isFavori={true} onToggleFavori={toggleFavori} cardStyle={cardStyle} />
                        ))}
                    </div>
                    <hr style={{ margin: '24px 0 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                </div>
            )}

            {/* === TÜM RAPORLAR === */}
            {filteredRaporlar.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <p style={{ margin: 0, fontWeight: 500 }}>Aramanızla eşleşen rapor bulunamadı.</p>
                </div>
            ) : (
                <div className="rapor-kartlar-grid">
                    {filteredRaporlar.map(r => (
                        <RaporKartItem key={r.id} r={r} isFavori={favoriRaporlar.includes(r.id)} onToggleFavori={toggleFavori} cardStyle={cardStyle} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProjeRaporlari;
